"use strict";
// ==========================================
// PML Modlist Console Dashboard Code
// ==========================================
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const valid_1 = __importDefault(require("semver/functions/valid"));
// ====================
// STATE & VARIABLES
// ====================
let apiKey = localStorage.getItem('pml_api_key') || '';
let apiUrl = localStorage.getItem('pml_api_url') || 'http://mods.polymodloader.com';
let userPermLevel = 0; // 0 = Guest, 1 = Admin, 2 = Moderator, 3 = Modder
let userName = '';
// Cache for mods to populate dropdowns etc.
let cachedMods = [];
// ====================
// UTILITY FUNCTIONS
// ====================
function parseJSONField(field, defaultValue) {
    if (field === undefined || field === null)
        return defaultValue;
    if (typeof field === 'string') {
        try {
            return JSON.parse(field);
        }
        catch (e) {
            console.error('Failed to parse JSON field:', field, e);
            return defaultValue;
        }
    }
    return field;
}
// Toast notification helper
function showToast(message, type = 'success') {
    const container = document.getElementById('toast-container');
    if (!container)
        return;
    const toast = document.createElement('div');
    toast.className = `custom-toast p-3 mb-2 d-flex justify-content-between align-items-center`;
    let borderStyle = 'var(--primary-accent)';
    if (type === 'danger')
        borderStyle = 'var(--danger-color)';
    if (type === 'warning')
        borderStyle = 'var(--warning-color)';
    if (type === 'success')
        borderStyle = 'var(--success-color)';
    toast.style.borderLeftColor = borderStyle;
    toast.innerHTML = `
        <div class="d-flex align-items-center">
            <span class="me-2 fw-semibold text-${type === 'success' ? 'success' : type === 'danger' ? 'danger' : 'warning'}">
                ${type === 'success' ? '✓' : type === 'danger' ? '✗' : '⚠'}
            </span>
            <div>${message}</div>
        </div>
        <button type="button" class="btn-close btn-close-white ms-3" aria-label="Close"></button>
    `;
    container.appendChild(toast);
    // Fade in
    setTimeout(() => toast.classList.add('show'), 50);
    const closeBtn = toast.querySelector('.btn-close');
    closeBtn === null || closeBtn === void 0 ? void 0 : closeBtn.addEventListener('click', () => {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 300);
    });
    // Auto dismiss after 4 seconds
    setTimeout(() => {
        if (toast.parentNode) {
            toast.classList.remove('show');
            setTimeout(() => toast.remove(), 300);
        }
    }, 4000);
}
// ====================
// API FUNCTIONS
// ====================
// Determine the permission level of the current key
async function verifyCredentials(url, key) {
    if (!key)
        return { valid: false, level: 0 };
    // Clean trailing slashes
    const baseUrl = url.replace(/\/+$/, '');
    try {
        const res = await fetch(`${baseUrl}/api/keys/self`, {
            headers: { 'Authorization': `Bearer ${key}` }
        });
        if (res.status === 200) {
            const data = await res.json();
            if (data.active) {
                return { valid: true, level: data.perm_level, user: data.user };
            }
        }
        return { valid: false, level: 0 };
    }
    catch (e) {
        console.error('Error verifying credentials:', e);
        return { valid: false, level: 0 };
    }
}
// Fetch all registered mods
async function getMods() {
    try {
        const response = await fetch(`${apiUrl}/api/mods`);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        // Handle D1 wrapping object format { results: [...] }
        const rows = Array.isArray(data) ? data : (data.results || []);
        cachedMods = rows.map((rawMod) => ({
            mod_id: rawMod.mod_id,
            url: rawMod.url,
            name: rawMod.name,
            description: rawMod.description || '',
            authors: parseJSONField(rawMod.authors, []),
            game_versions: parseJSONField(rawMod.game_versions, []),
            latest: parseJSONField(rawMod.latest, {}),
            tags: parseJSONField(rawMod.tags, []),
            icon_url: rawMod.icon_url || ''
        }));
        return cachedMods;
    }
    catch (e) {
        console.error('Failed to fetch mods:', e);
        showToast('Failed to fetch mods from API', 'danger');
        return [];
    }
}
// Get versions for a specific mod (or all versions)
async function getVersions(modId) {
    try {
        const url = modId ? `${apiUrl}/api/mods/${modId}/versions` : `${apiUrl}/api/versions`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        const rows = Array.isArray(data) ? data : (data.results || []);
        return rows.map((rawVersion) => ({
            id: rawVersion.id,
            mod_id: rawVersion.mod_id,
            version: rawVersion.version,
            game_versions: parseJSONField(rawVersion.game_versions, []),
            // API returns dependency array as [{id, version}, ...]
            dependencies: parseJSONField(rawVersion.dependencies, []),
            verified: rawVersion.verified === 1,
            verified_at: rawVersion.verified_at,
            verified_by: rawVersion.verified_by
        }));
    }
    catch (e) {
        console.error('Failed to fetch versions:', e);
        showToast('Failed to fetch versions from API', 'danger');
        return [];
    }
}
// Generate an API Key (Admin only)
async function generateKey(user, permLevel) {
    try {
        const response = await fetch(`${apiUrl}/api/keys/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify({ user, perm_level: permLevel })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to generate key');
        }
        return await response.json();
    }
    catch (e) {
        showToast(e.message, 'danger');
        return null;
    }
}
// Add a manual pre-generated API key
async function addKey(user, keyVal, permLevel) {
    try {
        const response = await fetch(`${apiUrl}/api/keys`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify({ user, key: keyVal, perm_level: permLevel })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to add key');
        }
        showToast('API Key registered successfully');
        return true;
    }
    catch (e) {
        showToast(e.message, 'danger');
        return false;
    }
}
// Retrieve list of keys (Admin only)
async function getKeys() {
    try {
        const response = await fetch(`${apiUrl}/api/keys`, {
            headers: { 'Authorization': `Bearer ${apiKey}` }
        });
        if (!response.ok)
            throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return Array.isArray(data) ? data : (data.results || []);
    }
    catch (e) {
        console.error('Failed to fetch keys:', e);
        return [];
    }
}
// ====================
// APPLICATION LAYOUT RENDERERS
// ====================
const app = document.getElementById('app');
function initApp() {
    app.innerHTML = `
        <div class="container py-4 h-100">
            <header class="d-flex flex-wrap justify-content-between align-items-center pb-3 mb-4 border-bottom" style="border-color: var(--border-color) !important;">
                <div class="d-flex align-items-center">
                    <span class="fs-4 fw-bold text-light me-3">PML Modlist Console</span>
                </div>
                <div id="user-status-container"></div>
            </header>

            <div class="row">
                <div class="col-lg-3 mb-4">
                    <div class="card p-3">
                        <ul class="nav nav-pills flex-column" id="nav-tabs" role="tablist">
                            <li class="nav-item mb-2">
                                <button class="nav-link w-100 text-start active" id="tab-credentials" data-tab="credentials">Credentials & Status</button>
                            </li>
                            <li class="nav-item mb-2">
                                <button class="nav-link w-100 text-start" id="tab-mods" data-tab="mods">Mod Registrations</button>
                            </li>
                            <li class="nav-item mb-2">
                                <button class="nav-link w-100 text-start" id="tab-versions" data-tab="versions">Versions & Verification</button>
                            </li>
                            <li class="nav-item mb-2">
                                <button class="nav-link w-100 text-start d-none" id="tab-addmod" data-tab="addmod">Register Mod</button>
                            </li>
                            <li class="nav-item mb-2">
                                <button class="nav-link w-100 text-start d-none" id="tab-addversion" data-tab="addversion">Submit Version</button>
                            </li>
                            <li class="nav-item mb-2">
                                <button class="nav-link w-100 text-start d-none" id="tab-keys" data-tab="keys">API Key Manager</button>
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="col-lg-9">
                    <div class="card p-4" id="workspace-content"></div>
                </div>
            </div>
        </div>
    `;
    setupNavigation();
    updatePermissionUI();
    // Auto-verify stored credentials on startup
    if (apiKey && apiUrl) {
        const statusContainer = document.getElementById('user-status-container');
        statusContainer.innerHTML = `<span class="badge bg-secondary-subtle perm-badge"><span class="spinner-border spinner-border-sm me-1" style="width: 10px; height: 10px;"></span>Authenticating...</span>`;
        verifyCredentials(apiUrl, apiKey).then(verification => {
            userPermLevel = verification.valid ? verification.level : 0;
            userName = verification.valid ? (verification.user || '') : '';
            updatePermissionUI();
            // Refresh Credentials tab if active to display permission details
            const activeTabButton = document.querySelector('#nav-tabs .nav-link.active');
            if (activeTabButton && activeTabButton.getAttribute('data-tab') === 'credentials') {
                const workspace = document.getElementById('workspace-content');
                renderCredentialsTab(workspace);
            }
        });
    }
    // Initial tab load
    switchTab('credentials');
}
function updatePermissionUI() {
    const statusContainer = document.getElementById('user-status-container');
    const tabAddMod = document.getElementById('tab-addmod');
    const tabAddVersion = document.getElementById('tab-addversion');
    const tabKeys = document.getElementById('tab-keys');
    // 1 = Admin, 2 = Moderator, 3 = Modder, 0 = Guest
    let badgeHtml = '';
    if (userPermLevel === 1) {
        badgeHtml = `<span class="badge bg-success-subtle perm-badge">Level 1 - Admin${userName ? ` (${userName})` : ''}</span>`;
        tabAddMod.classList.remove('d-none');
        tabAddVersion.classList.remove('d-none');
        tabKeys.classList.remove('d-none');
    }
    else if (userPermLevel === 2) {
        badgeHtml = `<span class="badge bg-info-subtle perm-badge">Level 2 - Moderator${userName ? ` (${userName})` : ''}</span>`;
        tabAddMod.classList.remove('d-none');
        tabAddVersion.classList.remove('d-none');
        tabKeys.classList.add('d-none');
    }
    else if (userPermLevel === 3) {
        badgeHtml = `<span class="badge bg-warning-subtle perm-badge">Level 3 - Modder${userName ? ` (${userName})` : ''}</span>`;
        tabAddMod.classList.add('d-none');
        tabAddVersion.classList.remove('d-none');
        tabKeys.classList.add('d-none');
    }
    else {
        badgeHtml = `<span class="badge bg-danger-subtle perm-badge">Guest (Read Only)</span>`;
        tabAddMod.classList.add('d-none');
        tabAddVersion.classList.add('d-none');
        tabKeys.classList.add('d-none');
    }
    statusContainer.innerHTML = badgeHtml;
}
function setupNavigation() {
    const navTabs = document.getElementById('nav-tabs');
    navTabs.addEventListener('click', (e) => {
        const target = e.target;
        const tabName = target.getAttribute('data-tab');
        if (tabName) {
            switchTab(tabName);
        }
    });
}
function switchTab(tabName, options) {
    // Update active state in nav pills
    const tabs = document.querySelectorAll('#nav-tabs .nav-link');
    tabs.forEach(tab => {
        if (tab.getAttribute('data-tab') === tabName) {
            tab.classList.add('active');
        }
        else {
            tab.classList.remove('active');
        }
    });
    const workspace = document.getElementById('workspace-content');
    workspace.innerHTML = `
        <div class="d-flex justify-content-center align-items-center py-5">
            <div class="spinner-border spinner-accent" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    `;
    // Render appropriate contents
    switch (tabName) {
        case 'credentials':
            renderCredentialsTab(workspace);
            break;
        case 'mods':
            renderModsTab(workspace);
            break;
        case 'versions':
            renderVersionsTab(workspace, options === null || options === void 0 ? void 0 : options.filterModId);
            break;
        case 'addmod':
            renderAddModTab(workspace, options === null || options === void 0 ? void 0 : options.editModId);
            break;
        case 'addversion':
            renderAddVersionTab(workspace, options);
            break;
        case 'keys':
            renderKeysTab(workspace);
            break;
    }
}
// ====================
// TAB 1: CREDENTIALS & STATUS
// ====================
function renderCredentialsTab(container) {
    container.innerHTML = `
        <h3 class="mb-4 text-light">Credentials & API Status</h3>
        <p class="text-secondary mb-4">Configure your connection credentials here. If you possess an API Key, inputting it will authenticate your console session to unlock mod registration, version submission, and administrative controls.</p>

        <div class="row g-4">
            <div class="col-md-7">
                <div class="mb-3">
                    <label for="input-api-url" class="form-label">API Endpoints URL</label>
                    <input type="text" class="form-control" id="input-api-url" placeholder="e.g. http://mods.polymodloader.com" value="${apiUrl}">
                </div>
                <div class="mb-4">
                    <label for="input-api-key" class="form-label">API Key / Token</label>
                    <input type="password" class="form-control" id="input-api-key" placeholder="Enter key (Bearer Token format)" value="${apiKey}">
                </div>
                <button id="btn-save-verify" class="btn btn-primary">Save & Verify Credentials</button>
            </div>
            
            <div class="col-md-5">
                <div class="card p-3 h-100 bg-black bg-opacity-25 border-0">
                    <h5 class="text-light mb-3">Permissions Status</h5>
                    <div id="status-card-inner">
                        <div class="text-secondary">Please click "Save & Verify Credentials" to query status.</div>
                    </div>
                </div>
            </div>
        </div>
    `;
    const statusCardInner = document.getElementById('status-card-inner');
    // Populate status card based on current cache
    updateStatusCard(statusCardInner);
    const saveBtn = document.getElementById('btn-save-verify');
    saveBtn.addEventListener('click', async () => {
        const urlInput = document.getElementById('input-api-url').value.trim();
        const keyInput = document.getElementById('input-api-key').value.trim();
        if (!urlInput) {
            showToast('API URL is required', 'danger');
            return;
        }
        saveBtn.disabled = true;
        saveBtn.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status"></span>Checking...`;
        statusCardInner.innerHTML = `<div class="d-flex justify-content-center py-3"><div class="spinner-border spinner-border-sm spinner-accent"></div></div>`;
        const verification = await verifyCredentials(urlInput, keyInput);
        apiUrl = urlInput;
        apiKey = keyInput;
        localStorage.setItem('pml_api_url', apiUrl);
        localStorage.setItem('pml_api_key', apiKey);
        userPermLevel = verification.valid ? verification.level : 0;
        userName = verification.valid ? (verification.user || '') : '';
        updatePermissionUI();
        updateStatusCard(statusCardInner);
        if (verification.valid) {
            showToast('Connection verified successfully!', 'success');
        }
        else if (keyInput) {
            showToast('Invalid credentials provided.', 'danger');
        }
        else {
            showToast('Using public Read-Only access.', 'warning');
        }
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save & Verify Credentials';
    });
}
function updateStatusCard(card) {
    if (userPermLevel === 1) {
        card.innerHTML = `
            <div class="d-flex align-items-center mb-3">
                <span class="badge bg-success-subtle me-2">Admin</span>
                <span class="text-success fw-semibold">Authenticated${userName ? ` as ${userName}` : ''}</span>
            </div>
            <p class="text-secondary small">You hold full Level 1 access. Permissions include:</p>
            <ul class="text-secondary small ps-3">
                <li>Create, deactivate, reactivate and delete API keys.</li>
                <li>Register new mods or modify existing entries.</li>
                <li>Verify, invalidate, or delete any mod version.</li>
            </ul>
        `;
    }
    else if (userPermLevel === 2) {
        card.innerHTML = `
            <div class="d-flex align-items-center mb-3">
                <span class="badge bg-info-subtle me-2">Moderator</span>
                <span class="text-info fw-semibold">Authenticated${userName ? ` as ${userName}` : ''}</span>
            </div>
            <p class="text-secondary small">You hold Level 2 access. Permissions include:</p>
            <ul class="text-secondary small ps-3">
                <li>Register new mods or modify existing metadata.</li>
                <li>Verify, invalidate, or delete any version.</li>
            </ul>
        `;
    }
    else if (userPermLevel === 3) {
        card.innerHTML = `
            <div class="d-flex align-items-center mb-3">
                <span class="badge bg-warning-subtle me-2">Modder</span>
                <span class="text-warning fw-semibold">Authenticated${userName ? ` as ${userName}` : ''}</span>
            </div>
            <p class="text-secondary small">You hold Level 3 access. Permissions include:</p>
            <ul class="text-secondary small ps-3">
                <li>Submit new versions to mods you are listed as an author of.</li>
            </ul>
        `;
    }
    else {
        card.innerHTML = `
            <div class="d-flex align-items-center mb-3">
                <span class="badge bg-danger-subtle me-2">Guest</span>
                <span class="text-danger fw-semibold">Read Only</span>
            </div>
            <p class="text-secondary small">You have not supplied a valid API credential. You are limited to viewing registered mods and versions.</p>
        `;
    }
}
// ====================
// TAB 2: MOD REGISTRATIONS
// ====================
function renderModsTab(container) {
    container.innerHTML = `
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h3 class="text-light m-0">Mod Registrations</h3>
            ${userPermLevel === 1 || userPermLevel === 2 ? '<button id="btn-create-mod" class="btn btn-primary btn-sm">+ Register New Mod</button>' : ''}
        </div>

        <div class="mb-4">
            <input type="text" id="search-mods" class="form-control" placeholder="Search by mod ID, name, authors, or tags...">
        </div>

        <div class="table-responsive">
            <table class="table table-hover align-middle">
                <thead>
                    <tr>
                        <th style="width: 60px;">Icon</th>
                        <th>Mod ID / Name</th>
                        <th>Authors</th>
                        <th>Game Versions</th>
                        <th>Tags</th>
                        <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody id="mods-table-body">
                    <tr>
                        <td colspan="6" class="text-center py-4 text-secondary">
                            <span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading mods...
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;
    const createBtn = document.getElementById('btn-create-mod');
    if (createBtn) {
        createBtn.addEventListener('click', () => {
            switchTab('addmod');
        });
    }
    getMods().then(mods => {
        const tbody = document.getElementById('mods-table-body');
        if (!tbody)
            return;
        if (mods.length === 0) {
            tbody.innerHTML = `<tr><td colspan="6" class="text-center py-4 text-secondary">No mods registered in database.</td></tr>`;
            return;
        }
        function populateTable(filteredMods, tbody) {
            if (filteredMods.length === 0) {
                tbody.innerHTML = `<tr><td colspan="6" class="text-center py-4 text-secondary">No matching mods found.</td></tr>`;
                return;
            }
            tbody.innerHTML = '';
            filteredMods.forEach(mod => {
                const tr = document.createElement('tr');
                const iconHtml = mod.icon_url
                    ? `<img src="${mod.icon_url}" class="bg-black bg-opacity-25" style="width: 36px; height: 36px; object-fit: contain;" onerror="this.src='data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%2736%27 height=%2736%27><rect width=%2736%27 height=%2736%27 fill=%27%23262b36%27/><text x=%2750%%27 y=%2755%%27 font-family=%27sans-serif%27 font-size=%2712%27 fill=%27%239ca3af%27 text-anchor=%27middle%27 font-weight=%27bold%27>${mod.name.substring(0, 2).toUpperCase()}</text></svg>'">`
                    : `<div class="rounded bg-secondary bg-opacity-25 d-flex align-items-center justify-content-center fw-bold text-secondary" style="width: 36px; height: 36px; font-size: 0.85rem;">${mod.name.substring(0, 2).toUpperCase()}</div>`;
                const authorsHtml = mod.authors.map(a => `<span class="badge bg-secondary me-1" style="font-size: 0.75rem;">${a}</span>`).join('');
                const versionsHtml = mod.game_versions.map(v => `<span class="badge bg-info-subtle me-1" style="font-size: 0.75rem;">${v}</span>`).join('');
                const tagsHtml = (mod.tags || []).map(t => `<span class="badge bg-dark border border-secondary text-secondary me-1" style="font-size: 0.75rem;">${t}</span>`).join('');
                const canEdit = userPermLevel === 1 || userPermLevel === 2;
                const canDelete = userPermLevel === 1 || userPermLevel === 2;
                tr.innerHTML = `
                    <td>${iconHtml}</td>
                    <td>
                        <div class="fw-bold text-light">${mod.name}</div>
                        <code class="text-secondary" style="font-size: 0.8rem;">${mod.mod_id}</code>
                    </td>
                    <td>${authorsHtml}</td>
                    <td>${versionsHtml}</td>
                    <td>${tagsHtml}</td>
                    <td class="text-end">
                        <div class="d-flex justify-content-end gap-1">
                            <button class="btn btn-secondary btn-sm btn-view-versions" data-id="${mod.mod_id}" title="View versions">Versions</button>
                            ${canEdit ? `<button class="btn btn-secondary btn-sm btn-edit-mod" data-id="${mod.mod_id}" title="Edit metadata">Edit</button>` : ''}
                            ${canDelete ? `<button class="btn btn-outline-danger btn-sm btn-delete-mod" data-id="${mod.mod_id}" title="Delete mod">Delete</button>` : ''}
                        </div>
                    </td>
                `;
                tbody.appendChild(tr);
            });
            tbody.querySelectorAll('.btn-view-versions').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const id = e.target.getAttribute('data-id');
                    switchTab('versions', { filterModId: id });
                });
            });
            tbody.querySelectorAll('.btn-edit-mod').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const id = e.target.getAttribute('data-id');
                    switchTab('addmod', { editModId: id });
                });
            });
            tbody.querySelectorAll('.btn-delete-mod').forEach(btn => {
                btn.addEventListener('click', async (e) => {
                    const id = e.target.getAttribute('data-id');
                    if (confirm(`Are you absolutely sure you want to permanently delete the mod "${id}"? This will also purge all of its registered versions.`)) {
                        try {
                            const res = await fetch(`${apiUrl}/api/mods/${id}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${apiKey}` }
                            });
                            if (res.ok) {
                                showToast(`Mod "${id}" deleted successfully`);
                                renderModsTab(container);
                            }
                            else {
                                const err = await res.json();
                                showToast(err.error || 'Failed to delete mod', 'danger');
                            }
                        }
                        catch (err) {
                            showToast('Server connection failed', 'danger');
                        }
                    }
                });
            });
        }
        populateTable(mods, tbody);
        const searchInput = document.getElementById('search-mods');
        searchInput.addEventListener('input', () => {
            const query = searchInput.value.toLowerCase().trim();
            if (!query) {
                populateTable(mods, tbody);
                return;
            }
            const filtered = mods.filter(m => m.mod_id.toLowerCase().includes(query) ||
                m.name.toLowerCase().includes(query) ||
                m.authors.some(a => a.toLowerCase().includes(query)) ||
                (m.tags && m.tags.some(t => t.toLowerCase().includes(query))));
            populateTable(filtered, tbody);
        });
    });
}
// ====================
// TAB 3: VERSIONS & VERIFICATION
// ====================
function renderVersionsTab(container, filterModId = '', options) {
    container.innerHTML = `
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h3 class="text-light m-0">Versions & Verification</h3>
            ${userPermLevel >= 1 && userPermLevel <= 3 ? '<button id="btn-create-version" class="btn btn-primary btn-sm">+ Submit New Version</button>' : ''}
        </div>

        <div class="row g-3 mb-4 align-items-end">
            <div class="col-md-5">
                <label for="filter-mod-select" class="form-label">Filter by Mod ID</label>
                <select id="filter-mod-select" class="form-select">
                    <option value="">-- All Registered Mods --</option>
                </select>
            </div>
            <div class="col-md-7">
                <input type="text" id="search-versions" class="form-control" placeholder="Search by version or game versions...">
            </div>
        </div>

        <div class="table-responsive">
            <table class="table table-hover align-middle">
                <thead>
                    <tr>
                        <th>Mod ID</th>
                        <th>Version</th>
                        <th>Supported Game</th>
                        <th>Dependencies</th>
                        <th>Verification Status</th>
                        <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody id="versions-table-body">
                    <tr>
                        <td colspan="6" class="text-center py-4 text-secondary">
                            <span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading versions...
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;
    const createBtn = document.getElementById('btn-create-version');
    if (createBtn) {
        createBtn.addEventListener('click', () => {
            switchTab('addversion');
        });
    }
    const modSelect = document.getElementById('filter-mod-select');
    getMods().then(mods => {
        const select = document.getElementById('filter-mod-select');
        if (!select)
            return;
        mods.forEach(mod => {
            const opt = document.createElement('option');
            opt.value = mod.mod_id;
            opt.textContent = `${mod.name} (${mod.mod_id})`;
            if (mod.mod_id === filterModId) {
                opt.selected = true;
            }
            select.appendChild(opt);
        });
    });
    function loadVersionsList(targetModId) {
        const selectedMod = targetModId !== undefined ? targetModId : modSelect.value;
        const tbody = document.getElementById('versions-table-body');
        if (!tbody)
            return;
        tbody.innerHTML = `<tr><td colspan="6" class="text-center py-4 text-secondary"><span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading versions...</td></tr>`;
        getVersions(selectedMod || undefined).then(versions => {
            const tbodyCheck = document.getElementById('versions-table-body');
            if (!tbodyCheck)
                return;
            if (versions.length === 0) {
                tbodyCheck.innerHTML = `<tr><td colspan="6" class="text-center py-4 text-secondary">No versions found for selection.</td></tr>`;
                return;
            }
            function populateTable(filteredVersions, tbody) {
                if (filteredVersions.length === 0) {
                    tbody.innerHTML = `<tr><td colspan="6" class="text-center py-4 text-secondary">No matching versions found.</td></tr>`;
                    return;
                }
                tbody.innerHTML = '';
                let currentModId = '';
                filteredVersions.forEach(ver => {
                    if (ver.mod_id !== currentModId) {
                        currentModId = ver.mod_id;
                        // Header row: mod name left, "+ Add Version" button right
                        const headerTr = document.createElement('tr');
                        headerTr.innerHTML = `
                            <td colspan="5" class="bg-dark text-light fw-bold py-2 ps-3">
                                Mod: ${ver.mod_id}
                            </td>
                            <td class="bg-dark py-2 text-end pe-2">
                                ${userPermLevel >= 1 && userPermLevel <= 3
                            ? `<button class="btn btn-sm btn-primary btn-add-version-mod" data-mod-id="${ver.mod_id}" title="Add version to this mod">+ Add Version</button>`
                            : ''}
                            </td>`;
                        tbody.appendChild(headerTr);
                    }
                    const tr = document.createElement('tr');
                    const gvBadges = ver.game_versions.map(v => `<span class="badge bg-info-subtle me-1" style="font-size: 0.75rem;">${v}</span>`).join('');
                    // Format dependencies as id:version badges; optional ones get dashed/muted style
                    let depsHtml = '';
                    if (!ver.dependencies || ver.dependencies.length === 0) {
                        depsHtml = `<span class="text-secondary small">None</span>`;
                    }
                    else {
                        depsHtml = ver.dependencies.map(d => {
                            if (d.optional) {
                                return `<span class="badge me-1" style="font-size:0.75rem;border:1.5px dashed var(--bs-secondary-color,#6c757d);color:var(--bs-secondary-color,#6c757d);background:transparent;" title="Optional dependency">${d.id}:${d.version} <span style="opacity:0.6;font-size:0.7rem;">opt</span></span>`;
                            }
                            return `<span class="badge bg-dark border border-secondary text-secondary me-1" style="font-size:0.75rem;">${d.id}:${d.version}</span>`;
                        }).join('');
                    }
                    let verifyBadge = '';
                    if (ver.verified) {
                        const verifiedDate = ver.verified_at ? new Date(ver.verified_at).toLocaleDateString() : 'N/A';
                        verifyBadge = `
                            <span class="badge bg-success-subtle me-1">Verified</span>
                            <div class="text-secondary mt-1" style="font-size: 0.75rem;" title="Verified by ${ver.verified_by} at ${ver.verified_at}">
                                By: ${ver.verified_by} (${verifiedDate})
                            </div>
                        `;
                    }
                    else {
                        verifyBadge = `<span class="badge bg-warning-subtle">Unverified</span>`;
                    }
                    const isL2 = userPermLevel === 1 || userPermLevel === 2;
                    tr.innerHTML = `
                        <td><code class="text-light">${ver.mod_id}</code></td>
                        <td><strong class="text-light">${ver.version}</strong></td>
                        <td>${gvBadges}</td>
                        <td>${depsHtml}</td>
                        <td>${verifyBadge}</td>
                        <td class="text-end">
                            <div class="d-flex justify-content-end gap-1">
                                ${isL2 && !ver.verified ? `<button class="btn btn-outline-success btn-sm btn-verify" data-id="${ver.id}" title="Approve and verify version">Verify</button>` : ''}
                                ${isL2 && ver.verified ? `<button class="btn btn-outline-danger btn-sm btn-invalidate" data-id="${ver.id}" title="Revoke verification">Invalidate</button>` : ''}
                                ${isL2 ? `<button class="btn btn-outline-danger btn-sm btn-delete-ver" data-id="${ver.id}" title="Delete version">Delete</button>` : ''}
                            </div>
                        </td>
                    `;
                    tbody.appendChild(tr);
                });
                tbody.querySelectorAll('.btn-add-version-mod').forEach(btn => {
                    btn.addEventListener('click', () => {
                        const modId = btn.getAttribute('data-mod-id');
                        switchTab('addversion', { filterModId: modId });
                    });
                });
                tbody.querySelectorAll('.btn-verify').forEach(btn => {
                    btn.addEventListener('click', async (e) => {
                        const id = e.target.getAttribute('data-id');
                        try {
                            const res = await fetch(`${apiUrl}/api/versions/verify/${id}`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${apiKey}` }
                            });
                            if (res.ok) {
                                showToast(`Version verified successfully`);
                                loadVersionsList();
                            }
                            else {
                                const err = await res.json();
                                showToast(err.error || 'Failed to verify version', 'danger');
                            }
                        }
                        catch (err) {
                            showToast('Server connection failed', 'danger');
                        }
                    });
                });
                tbody.querySelectorAll('.btn-invalidate').forEach(btn => {
                    btn.addEventListener('click', async (e) => {
                        const id = e.target.getAttribute('data-id');
                        try {
                            const res = await fetch(`${apiUrl}/api/versions/invalidate/${id}`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${apiKey}` }
                            });
                            if (res.ok) {
                                showToast(`Verification revoked`);
                                loadVersionsList();
                            }
                            else {
                                const err = await res.json();
                                showToast(err.error || 'Failed to invalidate verification', 'danger');
                            }
                        }
                        catch (err) {
                            showToast('Server connection failed', 'danger');
                        }
                    });
                });
                tbody.querySelectorAll('.btn-delete-ver').forEach(btn => {
                    btn.addEventListener('click', async (e) => {
                        const id = e.target.getAttribute('data-id');
                        if (confirm('Are you sure you want to permanently delete this version record?')) {
                            try {
                                const res = await fetch(`${apiUrl}/api/versions/${id}`, {
                                    method: 'DELETE',
                                    headers: { 'Authorization': `Bearer ${apiKey}` }
                                });
                                if (res.ok) {
                                    showToast('Version record deleted successfully');
                                    loadVersionsList();
                                }
                                else {
                                    const err = await res.json();
                                    showToast(err.error || 'Failed to delete version', 'danger');
                                }
                            }
                            catch (err) {
                                showToast('Server connection failed', 'danger');
                            }
                        }
                    });
                });
            }
            populateTable(versions, tbody);
            const searchInput = document.getElementById('search-versions');
            searchInput.addEventListener('input', () => {
                const query = searchInput.value.toLowerCase().trim();
                if (!query) {
                    populateTable(versions, tbody);
                    return;
                }
                const filtered = versions.filter(v => v.version.toLowerCase().includes(query) ||
                    v.mod_id.toLowerCase().includes(query) ||
                    v.game_versions.some(gv => gv.toLowerCase().includes(query)));
                populateTable(filtered, tbody);
            });
        });
    }
    loadVersionsList(filterModId);
    modSelect.addEventListener('change', () => {
        loadVersionsList();
    });
}
// ====================
// TAB 4: REGISTER / EDIT MOD (Level 2+)
// ====================
function renderAddModTab(container, editModId) {
    const isEditMode = !!editModId;
    const targetMod = isEditMode ? cachedMods.find(m => m.mod_id === editModId) : null;
    if (isEditMode && !targetMod) {
        showToast('Mod metadata not loaded. Refreshing...', 'warning');
        switchTab('mods');
        return;
    }
    container.innerHTML = `
        <h3 class="mb-4 text-light">${isEditMode ? `Edit Mod: ${editModId}` : 'Register New Mod'}</h3>
        <p class="text-secondary mb-4">Complete details to insert/patch mod definitions. Game Versions and Latest entries are validated as correct SemVer strings.</p>

        <form id="form-mod" class="needs-validation">
            <div class="row g-3">
                <div class="col-md-6">
                    <label for="input-mod-id" class="form-label">Mod Unique Identifier (Mod ID)</label>
                    <input type="text" class="form-control" id="input-mod-id" placeholder="e.g. polytrack-core" ${isEditMode ? 'disabled' : 'required'} value="${(targetMod === null || targetMod === void 0 ? void 0 : targetMod.mod_id) || ''}">
                    <div class="form-text text-secondary">Lowercase words separated by hyphens (cannot be changed later).</div>
                </div>
                <div class="col-md-6">
                    <label for="input-mod-name" class="form-label">Mod Display Name</label>
                    <input type="text" class="form-control" id="input-mod-name" placeholder="e.g. Polytrack Core Mod" required value="${(targetMod === null || targetMod === void 0 ? void 0 : targetMod.name) || ''}">
                </div>
                <div class="col-md-12">
                    <label for="input-mod-desc" class="form-label">Short description</label>
                    <input type="text" class="form-control" id="input-mod-desc" value="${(targetMod === null || targetMod === void 0 ? void 0 : targetMod.description) || ''}">
                </div>
                <div class="col-md-12">
                    <label for="input-mod-url" class="form-label">Import URL</label>
                    <input type="url" class="form-control" id="input-mod-url" placeholder="e.g. https://github.com/user/repo" required value="${(targetMod === null || targetMod === void 0 ? void 0 : targetMod.url) || ''}">
                </div>
                <div class="col-md-6">
                    <label for="input-mod-authors" class="form-label">Authors (Comma separated)</label>
                    <input type="text" class="form-control" id="input-mod-authors" placeholder="e.g. AuthorName1, AuthorName2" required value="${targetMod ? targetMod.authors.join(', ') : ''}">
                </div>
                <div class="col-md-6">
                    <label for="input-mod-game-versions" class="form-label">Supported Game Versions (Comma separated)</label>
                    <input type="text" class="form-control" id="input-mod-game-versions" placeholder="e.g. 0.6.0, 0.5.2" required value="${targetMod ? targetMod.game_versions.join(', ') : ''}">
                </div>
                <div class="col-md-12">
                    <label for="input-mod-latest" class="form-label">Latest Version Mapping (Comma-separated Key:Value)</label>
                    <input type="text" class="form-control" id="input-mod-latest" placeholder="e.g. 0.6.0:1.3.2, 0.5.2:1.1.0" required value="${targetMod ? Object.entries(targetMod.latest).map(([k, v]) => `${k}:${v}`).join(', ') : ''}">
                    <div class="form-text text-secondary">Specifies latest mod release version mapped to game versions.</div>
                </div>
                <div class="col-md-6">
                    <label for="input-mod-tags" class="form-label">Tags / Categories (Comma separated)</label>
                    <input type="text" class="form-control" id="input-mod-tags" placeholder="e.g. gameplay, tool, library" value="${targetMod ? (targetMod.tags || []).join(', ') : ''}">
                </div>
                <div class="col-md-6">
                    <label for="input-mod-icon-url" class="form-label">Icon URL (Optional)</label>
                    <input type="url" class="form-control" id="input-mod-icon-url" placeholder="e.g. https://domain.com/icon.png" value="${(targetMod === null || targetMod === void 0 ? void 0 : targetMod.icon_url) || ''}">
                </div>

                <div class="col-md-12 mt-4">
                    <button type="submit" class="btn btn-primary" id="btn-submit-mod">${isEditMode ? 'Save Mod Changes' : 'Register Mod'}</button>
                    <button type="button" class="btn btn-secondary ms-2" id="btn-cancel-mod">Cancel</button>
                </div>
            </div>
        </form>
    `;
    document.getElementById('btn-cancel-mod').addEventListener('click', () => {
        switchTab('mods');
    });
    const form = document.getElementById('form-mod');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const modIdInput = document.getElementById('input-mod-id');
        const modId = modIdInput.value.trim();
        const name = document.getElementById('input-mod-name').value.trim();
        const description = document.getElementById('input-mod-desc').value.trim();
        const url = document.getElementById('input-mod-url').value.trim();
        const authors = document.getElementById('input-mod-authors').value.split(',').map(s => s.trim()).filter(Boolean);
        const game_versions = document.getElementById('input-mod-game-versions').value.split(',').map(s => s.trim()).filter(Boolean);
        const tags = document.getElementById('input-mod-tags').value.split(',').map(s => s.trim()).filter(Boolean);
        const icon_url = document.getElementById('input-mod-icon-url').value.trim() || null;
        const latestInputVal = document.getElementById('input-mod-latest').value.trim();
        const latest = {};
        const mappings = latestInputVal.split(',').map(s => s.trim()).filter(Boolean);
        for (const mapping of mappings) {
            const parts = mapping.split(':');
            if (parts.length !== 2) {
                showToast(`Invalid mapping format in Latest: "${mapping}". Use game_ver:mod_ver format.`, 'danger');
                return;
            }
            const gVer = parts[0].trim();
            const mVer = parts[1].trim();
            if (!(0, valid_1.default)(gVer)) {
                showToast(`Invalid SemVer for game version: "${gVer}"`, 'danger');
                return;
            }
            if (!(0, valid_1.default)(mVer)) {
                showToast(`Invalid SemVer for mod version: "${mVer}"`, 'danger');
                return;
            }
            latest[gVer] = mVer;
        }
        for (const gv of game_versions) {
            if (!(0, valid_1.default)(gv)) {
                showToast(`Invalid SemVer in game versions: "${gv}"`, 'danger');
                return;
            }
        }
        const submitBtn = document.getElementById('btn-submit-mod');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Saving...';
        const payload = {
            url,
            name,
            description,
            authors,
            game_versions,
            latest,
            tags,
            icon_url
        };
        if (!isEditMode) {
            payload.mod_id = modId;
        }
        const requestUrl = isEditMode ? `${apiUrl}/api/mods/${editModId}` : `${apiUrl}/api/mods`;
        const method = isEditMode ? 'PATCH' : 'POST';
        try {
            const res = await fetch(requestUrl, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${apiKey}`
                },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                showToast(`Mod "${isEditMode ? editModId : modId}" saved successfully`);
                switchTab('mods');
            }
            else {
                const err = await res.json();
                showToast(err.error || 'Operation failed', 'danger');
                submitBtn.disabled = false;
                submitBtn.textContent = isEditMode ? 'Save Mod Changes' : 'Register Mod';
            }
        }
        catch (err) {
            showToast('Server connection failed', 'danger');
            submitBtn.disabled = false;
            submitBtn.textContent = isEditMode ? 'Save Mod Changes' : 'Register Mod';
        }
    });
}
// ====================
// TAB 5: SUBMIT VERSION (Level 2+ or 3)
// ====================
function renderAddVersionTab(container, options) {
    container.innerHTML = `
        <h3 class="mb-4 text-light">Submit New Version</h3>
        <p class="text-secondary mb-4">Add a new release version for an existing mod registration. Note: Level 3 Modders can only publish releases for mods they are listed as an author of.</p>

        <form id="form-version">
            <div class="row g-3">
                <div class="col-md-6">
                    <label for="select-version-mod" class="form-label">Select Mod ID</label>
                    <select id="select-version-mod" class="form-select" required>
                        <option value="" disabled selected>-- Select Mod --</option>
                    </select>
                </div>
                <div class="col-md-6">
                    <label for="input-version-num" class="form-label">Release Version (SemVer)</label>
                    <input type="text" class="form-control" id="input-version-num" placeholder="e.g. 1.3.2" required>
                </div>
                <div class="col-md-12">
                    <label for="input-version-changelog" class="form-label">Version changelog</label>
                    <input type="text" class="form-control" id="input-version-changelog" placeholder="">
                </div>
                <div class="col-md-12">
                    <label class="form-label">Supported Game Versions</label>
                    <div id="game-version-picker" style="display:flex;flex-wrap:wrap;gap:8px;padding:10px 12px;background:var(--bg-color,#1a1f2e);border:1px solid var(--border-color,#343a40);border-radius:6px;min-height:48px;align-items:center;">
                        <span class="text-secondary small" id="gvp-loading">Loading versions...</span>
                    </div>
                    <div class="form-text text-secondary">Click to toggle. At least one version must be selected.</div>
                    <input type="hidden" id="select-version-game-hidden">
                </div>
                <div class="col-md-12">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <label class="form-label mb-0">Dependencies</label>
                        <button type="button" class="btn btn-sm btn-secondary" id="btn-add-dep">+ Add Dependency</button>
                    </div>
                    <div id="dep-rows" style="display:flex;flex-direction:column;gap:8px;"></div>
                    <div class="form-text text-secondary mt-1">Leave empty if this version has no dependencies.</div>
                </div>

                <div class="col-md-12 mt-4">
                    <button type="submit" class="btn btn-primary" id="btn-submit-version">Submit Version</button>
                    <button type="button" class="btn btn-secondary ms-2" id="btn-cancel-version">Cancel</button>
                </div>
            </div>
        </form>
    `;
    const modSelect = document.getElementById('select-version-mod');
    const gvPicker = document.getElementById('game-version-picker');
    const depRows = document.getElementById('dep-rows');
    // Dependency row builder
    document.getElementById('btn-add-dep').addEventListener('click', () => {
        const row = document.createElement('div');
        row.className = 'dep-row d-flex align-items-center gap-2';
        row.style.cssText = `
            padding: 8px 10px;
            background: var(--surface-color, #2a2f3e);
            border: 1px solid var(--border-color, #343a40);
            border-radius: 6px;
        `;
        row.innerHTML = `
            <input type="text" class="form-control form-control-sm dep-id" placeholder="Mod ID (e.g. polytrack-core)" style="flex:2;">
            <input type="text" class="form-control form-control-sm dep-version" placeholder="Constraint (e.g. ^1.0.0)" style="flex:1.5;">
            <div class="d-flex align-items-center gap-1 text-secondary" style="white-space:nowrap;font-size:0.8rem;">
                <input type="checkbox" class="form-check-input dep-optional mt-0" id="dep-opt-${Date.now()}" style="cursor:pointer;">
                <label class="form-check-label" for="dep-opt-${Date.now()}" style="cursor:pointer;">Optional</label>
            </div>
            <button type="button" class="btn btn-outline-danger btn-sm dep-remove" style="flex-shrink:0;">✕</button>
        `;
        row.querySelector('.dep-remove').addEventListener('click', () => row.remove());
        depRows.appendChild(row);
    });
    const selectedGameVersions = new Set();
    function renderChips(allVersions) {
        gvPicker.innerHTML = '';
        if (allVersions.length === 0) {
            gvPicker.innerHTML = '<span class="text-secondary small">No game versions available.</span>';
            return;
        }
        allVersions.forEach(v => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.textContent = v;
            chip.dataset.version = v;
            const isSelected = selectedGameVersions.has(v);
            chip.style.cssText = `
                padding: 4px 12px;
                border-radius: 20px;
                font-size: 0.8rem;
                font-family: Outfit, sans-serif;
                font-weight: 600;
                cursor: pointer;
                transition: background 0.15s, color 0.15s, border-color 0.15s, box-shadow 0.15s;
                border: 1.5px solid ${isSelected ? 'var(--border-focus)' : 'var(--border-color)'};
                background: var(--surface-color, #2a2f3e);
                color: ${isSelected ? 'var(--text-primary)' : 'var(--text-secondary)'};
                ${isSelected ? 'box-shadow: 0 0 0 1px rgba(13,202,240,0.3);' : ''}
            `;
            chip.addEventListener('click', () => {
                if (selectedGameVersions.has(v)) {
                    selectedGameVersions.delete(v);
                }
                else {
                    selectedGameVersions.add(v);
                }
                renderChips(allVersions);
            });
            gvPicker.appendChild(chip);
        });
    }
    getMods().then(mods => {
        const modSelectCheck = document.getElementById('select-version-mod');
        if (!modSelectCheck)
            return;
        const allGameVersions = Array.from(new Set(mods.flatMap(m => m.game_versions))).sort();
        const loadingEl = document.getElementById('gvp-loading');
        if (loadingEl)
            loadingEl.remove();
        renderChips(allGameVersions);
        // Level 3 modders only see mods they are authors of
        const selectableMods = userPermLevel === 3
            ? mods.filter(m => m.authors.some(a => a.toLowerCase() === userName.toLowerCase()))
            : mods;
        if (selectableMods.length === 0 && userPermLevel === 3) {
            const opt = document.createElement('option');
            opt.disabled = true;
            opt.textContent = '-- No mods authored by you found --';
            modSelect.appendChild(opt);
        }
        else {
            selectableMods.forEach(mod => {
                const opt = document.createElement('option');
                opt.value = mod.mod_id;
                opt.textContent = `${mod.name} (${mod.mod_id})`;
                modSelect.appendChild(opt);
            });
        }
        if (options === null || options === void 0 ? void 0 : options.filterModId) {
            modSelect.value = options.filterModId;
        }
    });
    document.getElementById('btn-cancel-version').addEventListener('click', () => {
        switchTab('versions');
    });
    const form = document.getElementById('form-version');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const mod_id = modSelect.value;
        const version = document.getElementById('input-version-num').value.trim();
        const game_versions = Array.from(selectedGameVersions);
        const changelog = document.getElementById('input-version-changelog').value.trim() || null;
        if (!(0, valid_1.default)(version)) {
            showToast(`Invalid SemVer format: "${version}"`, 'danger');
            return;
        }
        if (game_versions.length === 0) {
            showToast('Please select at least one supported game version.', 'danger');
            return;
        }
        for (const gv of game_versions) {
            if (!(0, valid_1.default)(gv)) {
                showToast(`Invalid SemVer for game version: "${gv}"`, 'danger');
                return;
            }
        }
        // Collect dependencies from builder rows
        const dependencies = [];
        const rows = depRows.querySelectorAll('.dep-row');
        for (const row of Array.from(rows)) {
            const depId = row.querySelector('.dep-id').value.trim();
            const depVersion = row.querySelector('.dep-version').value.trim();
            const depOptional = row.querySelector('.dep-optional').checked;
            if (!depId && !depVersion)
                continue; // skip completely empty rows silently
            if (!depId) {
                showToast('A dependency row is missing its Mod ID.', 'danger');
                return;
            }
            if (!depVersion) {
                showToast(`Dependency "${depId}" is missing a version constraint.`, 'danger');
                return;
            }
            dependencies.push({ id: depId, version: depVersion, optional: depOptional });
        }
        const submitBtn = document.getElementById('btn-submit-version');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Submitting...';
        try {
            const res = await fetch(`${apiUrl}/api/versions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${apiKey}`
                },
                body: JSON.stringify({
                    mod_id,
                    version,
                    game_versions,
                    dependencies,
                    changelog
                })
            });
            if (res.ok) {
                showToast('Version record added successfully');
                switchTab('versions', { filterModId: mod_id });
            }
            else {
                const err = await res.json();
                showToast(err.error || 'Submission failed', 'danger');
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit Version';
            }
        }
        catch (err) {
            showToast('Server connection failed', 'danger');
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit Version';
        }
    });
}
// ====================
// TAB 6: API KEY MANAGER (Level 1 Admin Only)
// ====================
function renderKeysTab(container) {
    container.innerHTML = `
        <h3 class="mb-4 text-light">API Key Manager</h3>
        <p class="text-secondary mb-4">View existing keys, revoke access, or register new credentials. Generated keys display their raw value once; ensure you copy it.</p>

        <div class="row g-4 mb-5">
            <div class="col-md-6">
                <div class="card p-3 h-100 bg-black bg-opacity-10">
                    <h5 class="text-light mb-3">Generate Random Key</h5>
                    <form id="form-gen-key">
                        <div class="mb-3">
                            <label for="input-gen-user" class="form-label">User name</label>
                            <input type="text" class="form-control" id="input-gen-user" placeholder="e.g. JohnModder" required>
                        </div>
                        <div class="mb-3">
                            <label for="select-gen-level" class="form-label">Permission Level</label>
                            <select id="select-gen-level" class="form-select" required>
                                <option value="" disabled selected>-- Select level --</option>
                                <option value="1">Level 1 - Admin</option>
                                <option value="2">Level 2 - Moderator</option>
                                <option value="3">Level 3 - Modder</option>
                            </select>
                        </div>
                        <button type="submit" class="btn btn-primary btn-sm">Generate & Register Key</button>
                    </form>
                </div>
            </div>

            <div class="col-md-6">
                <div class="card p-3 h-100 bg-black bg-opacity-10">
                    <h5 class="text-light mb-3">Add Pre-generated Key</h5>
                    <form id="form-add-key">
                        <div class="mb-3">
                            <label for="input-add-user" class="form-label">User name</label>
                            <input type="text" class="form-control" id="input-add-user" placeholder="e.g. AliceAdmin" required>
                        </div>
                        <div class="mb-3">
                            <label for="input-add-keyval" class="form-label">Key Value (min 32 chars)</label>
                            <input type="text" class="form-control" id="input-add-keyval" placeholder="Input token string" required minlength="32">
                        </div>
                        <div class="mb-3">
                            <label for="select-add-level" class="form-label">Permission Level</label>
                            <select id="select-add-level" class="form-select" required>
                                <option value="" disabled selected>-- Select level --</option>
                                <option value="1">Level 1 - Admin</option>
                                <option value="2">Level 2 - Moderator</option>
                                <option value="3">Level 3 - Modder</option>
                            </select>
                        </div>
                        <button type="submit" class="btn btn-secondary btn-sm text-light">Register Manual Key</button>
                    </form>
                </div>
            </div>
        </div>

        <div id="new-key-display" class="mb-4"></div>

        <h4 class="text-light mb-3">Registered API Credentials</h4>
        <div class="table-responsive">
            <table class="table table-hover align-middle">
                <thead>
                    <tr>
                        <th>User</th>
                        <th>Permission Level</th>
                        <th>Created At</th>
                        <th>Status</th>
                        <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody id="keys-table-body">
                    <tr>
                        <td colspan="5" class="text-center py-4 text-secondary">
                            <span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading API Keys...
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;
    const keysTableBody = document.getElementById('keys-table-body');
    const newKeyDisplay = document.getElementById('new-key-display');
    function loadKeysList() {
        getKeys().then(keys => {
            const tbodyCheck = document.getElementById('keys-table-body');
            if (!tbodyCheck || !keysTableBody)
                return;
            if (keys.length === 0) {
                keysTableBody.innerHTML = `<tr><td colspan="5" class="text-center py-4 text-secondary">No registered API keys found.</td></tr>`;
                return;
            }
            keysTableBody.innerHTML = '';
            keys.forEach(k => {
                const tr = document.createElement('tr');
                let lvlBadge = '';
                if (k.perm_level === 1)
                    lvlBadge = '<span class="badge bg-success-subtle">Admin (1)</span>';
                else if (k.perm_level === 2)
                    lvlBadge = '<span class="badge bg-info-subtle">Moderator (2)</span>';
                else if (k.perm_level === 3)
                    lvlBadge = '<span class="badge bg-warning-subtle">Modder (3)</span>';
                const statusBadge = k.active
                    ? '<span class="badge bg-success-subtle">Active</span>'
                    : '<span class="badge bg-danger-subtle">Inactive</span>';
                const actionBtn = k.active
                    ? `<button class="btn btn-secondary btn-sm btn-deactivate" data-user="${k.user}">Deactivate</button>`
                    : `<button class="btn btn-outline-success btn-sm btn-activate" data-user="${k.user}">Activate</button>`;
                const dateStr = new Date(k.created_at).toLocaleString();
                tr.innerHTML = `
                    <td><strong class="text-light">${k.user}</strong></td>
                    <td>${lvlBadge}</td>
                    <td><span class="text-secondary small">${dateStr}</span></td>
                    <td>${statusBadge}</td>
                    <td class="text-end">
                        <div class="d-flex justify-content-end gap-1">
                            ${actionBtn}
                            <button class="btn btn-outline-danger btn-sm btn-delete-key" data-user="${k.user}">Delete</button>
                        </div>
                    </td>
                `;
                keysTableBody.appendChild(tr);
            });
            keysTableBody.querySelectorAll('.btn-deactivate').forEach(btn => {
                btn.addEventListener('click', async (e) => {
                    const user = e.target.getAttribute('data-user');
                    try {
                        const res = await fetch(`${apiUrl}/api/keys/deactivate/${user}`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${apiKey}` }
                        });
                        if (res.ok) {
                            showToast(`Deactivated key for "${user}"`);
                            loadKeysList();
                        }
                        else {
                            const err = await res.json();
                            showToast(err.error || 'Failed to deactivate', 'danger');
                        }
                    }
                    catch (e) {
                        showToast('Connection error', 'danger');
                    }
                });
            });
            keysTableBody.querySelectorAll('.btn-activate').forEach(btn => {
                btn.addEventListener('click', async (e) => {
                    const user = e.target.getAttribute('data-user');
                    try {
                        const res = await fetch(`${apiUrl}/api/keys/activate/${user}`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${apiKey}` }
                        });
                        if (res.ok) {
                            showToast(`Reactivated key for "${user}"`);
                            loadKeysList();
                        }
                        else {
                            const err = await res.json();
                            showToast(err.error || 'Failed to activate', 'danger');
                        }
                    }
                    catch (e) {
                        showToast('Connection error', 'danger');
                    }
                });
            });
            keysTableBody.querySelectorAll('.btn-delete-key').forEach(btn => {
                btn.addEventListener('click', async (e) => {
                    const user = e.target.getAttribute('data-user');
                    if (confirm(`Are you sure you want to permanently delete the API key credentials for "${user}"?`)) {
                        try {
                            const res = await fetch(`${apiUrl}/api/keys/${user}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${apiKey}` }
                            });
                            if (res.ok) {
                                showToast(`Permanently deleted credentials for "${user}"`);
                                loadKeysList();
                            }
                            else {
                                const err = await res.json();
                                showToast(err.error || 'Failed to delete credentials', 'danger');
                            }
                        }
                        catch (e) {
                            showToast('Connection error', 'danger');
                        }
                    }
                });
            });
        });
    }
    loadKeysList();
    const formGen = document.getElementById('form-gen-key');
    formGen.addEventListener('submit', async (e) => {
        e.preventDefault();
        const user = document.getElementById('input-gen-user').value.trim();
        const level = parseInt(document.getElementById('select-gen-level').value);
        const res = await generateKey(user, level);
        if (res && res.key) {
            newKeyDisplay.innerHTML = `
                <div class="card border border-success p-3" style="background-color: rgba(16, 185, 129, 0.05);">
                    <h5 class="text-success mb-2">✓ Key Generated Successfully!</h5>
                    <p class="text-secondary small mb-3">Copy this key value now. It will not be shown again:</p>
                    <div class="input-group">
                        <input type="text" class="form-control font-monospace bg-dark text-light" id="generated-key-field" value="${res.key}" readonly>
                        <button class="btn btn-primary" id="btn-copy-gen-key">Copy Key</button>
                    </div>
                </div>
            `;
            document.getElementById('btn-copy-gen-key').addEventListener('click', () => {
                const f = document.getElementById('generated-key-field');
                f.select();
                navigator.clipboard.writeText(f.value);
                showToast('Key copied to clipboard!');
            });
            formGen.reset();
            loadKeysList();
        }
    });
    const formAdd = document.getElementById('form-add-key');
    formAdd.addEventListener('submit', async (e) => {
        e.preventDefault();
        const user = document.getElementById('input-add-user').value.trim();
        const val = document.getElementById('input-add-keyval').value.trim();
        const level = parseInt(document.getElementById('select-add-level').value);
        const success = await addKey(user, val, level);
        if (success) {
            formAdd.reset();
            loadKeysList();
        }
    });
}
// ====================
// INITIALIZATION ENTRY
// ====================
document.addEventListener('DOMContentLoaded', () => {
    initApp();
});
